import { useTranslation } from 'react-i18next';

/// keep the numeric code so we can handle the error on the BankError screen via routing.
export const BankIdErrorPropsMap = {
  0: 'ERROR',
  1: 'unknown_error',
  2: 'confirm_error',
  3: 'session_error',
  4: 'session_init_error',
  5: 'session_status_error',
  6: 'client_not_loaded',
  7: 'redirect_url_not_loaded',
  8: 'session_id_not_loaded',
  9: 'reject_error',
  10: 'PERSONAL_ID_MISMATCH',
  11: 'NAME_SURNAME_MISMATCH',
  12: 'INVALID_ACCOUNT_TYPE',
  13: 'ID_CARD_DUPLICATED',
  14: 'BANK_ACCOUNT_DUPLICATED',
  15: 'NATIONALITY_BLACKLISTED',
  16: 'FOREIGNER_NOT_ALLOWED',
} as const;

// Reverse lookup: returns the numeric string key for a given error value, or undefined if not found
export const getKeyByErrorValue = (value: string) => {
  return Object.entries(BankIdErrorPropsMap).find(([, v]) => v === value)?.[0];
};

// Forward lookup: returns the error string for a given numeric key (e.g. 1 → 'unknown_error')
export const getErrorValueByKey = (key: keyof typeof BankIdErrorPropsMap) => {
  return BankIdErrorPropsMap[key];
};

export type BankIdErrorProps =
  (typeof BankIdErrorPropsMap)[keyof typeof BankIdErrorPropsMap];

type Translation = {
  title: string;
  description: string;
  button: string;
};

export function useErrorTranslation(key: BankIdErrorProps): Translation {
  const { t } = useTranslation();

  const errorsBalkTranslation: Partial<Record<BankIdErrorProps, Translation>> =
    {
      FOREIGNER_NOT_ALLOWED: {
        title: t('bank_id_error:foreigner.title'),
        description: t('bank_id_error:foreigner.description'),
        button: t('bank_id_error:foreigner.continue'),
      },
      NATIONALITY_BLACKLISTED: {
        title: t('bank_id_error:foreigner.title'),
        description: t('bank_id_error:foreigner.description'),
        button: t('bank_id_error:foreigner.continue'),
      },
    };

  // Falls back to generic title/description for all unspecified error codes
  const defaultTranslation: Translation = {
    title: t('bank_id_error:default.title'),
    description: t('bank_id_error:default.description'),
    button: t('bank_id_error:default.continue'),
  };

  if (errorsBalkTranslation[key]) {
    return errorsBalkTranslation[key] as Translation;
  }

  return defaultTranslation;
}
